import { useCallback, useEffect, useRef, useState } from 'react';
import { Logo } from './Logo';
import { CloseIcon, MenuIcon } from './icons';
import { NAV_LINKS } from '../data/navigation';
import { useDialog } from '../hooks/useDialog';
import { ROUTES, sectionHref } from '../router';
import './SiteHeader.css';

const DESKTOP_QUERY = '(min-width: 900px)';

/**
 * Sticky header that settles into a floating island once the page moves, so the
 * primary action stays reachable from anywhere on the page.
 */
export const SiteHeader = ({ route = ROUTES.home }) => {
  const [settled, setSettled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    let frame = null;
    const update = () => {
      frame = null;
      setSettled(window.scrollY > 40);
    };
    const onScroll = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  // The panel is a mobile affordance only — never leave it open on desktop.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mediaQuery = window.matchMedia(DESKTOP_QUERY);
    const onChange = (event) => {
      if (event.matches) setMenuOpen(false);
    };
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', onChange);
      return () => mediaQuery.removeEventListener('change', onChange);
    }
    mediaQuery.addListener(onChange);
    return () => mediaQuery.removeListener(onChange);
  }, []);

  useDialog({ open: menuOpen, onClose: closeMenu, containerRef: menuRef });

  return (
    <header className={`tl-header${settled ? ' is-settled' : ''}${menuOpen ? ' is-open' : ''}`}>
      <div className="tl-header__rail">
        <div className="tl-header__inner">
          <a
            className="tl-header__brand"
            href={route === ROUTES.home ? '#top' : ROUTES.home}
            aria-label={route === ROUTES.home ? 'Tu-Link — back to top' : 'Tu-Link — home'}
          >
            <Logo width={140} alt="" />
          </a>

          <nav className="tl-header__nav" aria-label="Primary">
            <ul className="tl-header__list">
              {NAV_LINKS.map((link) => (
                <li key={link.href}>
                  <a className="tl-header__link" href={sectionHref(link.href, route)}>
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="tl-header__actions">
            <a
              className="tl-button tl-button--primary tl-header__cta"
              href={sectionHref('#get-tulink', route)}
            >
              Get Tulink
            </a>

            <button
              type="button"
              className="tl-header__toggle"
              aria-expanded={menuOpen}
              aria-controls="tl-mobile-menu"
              onClick={() => setMenuOpen((open) => !open)}
            >
              {menuOpen ? <CloseIcon /> : <MenuIcon />}
              <span className="tl-visually-hidden">{menuOpen ? 'Close menu' : 'Open menu'}</span>
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="tl-header__panel" id="tl-mobile-menu" ref={menuRef} tabIndex={-1}>
            <nav aria-label="Primary, mobile">
              <ul>
                {NAV_LINKS.map((link) => (
                  <li key={link.href}>
                    <a
                      className="tl-header__panel-link"
                      href={sectionHref(link.href, route)}
                      onClick={closeMenu}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
